import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRotateRight,
  faCheckDouble,
  faClockRotateLeft,
  faEye,
  faLock,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { SkeletonPage } from '@/components/skeletons';
import { toast, swalConfirm } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import type { ReportTemplateColumn } from '@/services/report-template.service';
import { useReportTemplate } from './hooks/useReportTemplates';
import {
  usePublishReportTemplate,
  useUpdateReportTemplateDesign,
} from './hooks/useReportTemplateMutations';
import { sameDesign, structuralMismatch, toDesignPayload, type DraftSection } from './design-draft';
import { TemplateSectionRow } from './components/TemplateSectionRow';
import { TemplateStructurePreview } from './components/TemplateStructurePreview';
import {
  ReportTemplateDefaultBadge,
  ReportTemplateStatusBadge,
  ReportTemplateSystemBadge,
} from './components/ReportTemplateStatusBadge';

function extractMsg(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function statusOf(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}

/**
 * The MSE Report Template Designer (B6.9e §6).
 *
 * ── What this screen is, and is not ─────────────────────────────────────────
 * It edits the MASTER TEMPLATE: "how should every report built from this design
 * behave?". It is not the SE report composer, which answers the entirely
 * different question "what should THIS inspection report contain?". The two
 * surfaces stay separate, and this phase changes neither the composer nor the
 * renderer.
 *
 * ── Authority ───────────────────────────────────────────────────────────────
 * The server owns every rule. This page renders what the API reports
 * (`canBeEngineerToggleable`, `isWidthEditable`, `minWidthFraction`,
 * `defaultTitle`, `status`) and re-seeds itself from the document the API
 * returns after a save. It never validates on the backend's behalf: publishing
 * sends the request and displays whatever the publish gate says, so the UI can
 * never drift into being a second, weaker authority.
 *
 * The single rule stated locally is D-079's locked structural visibility, which
 * the read API does not expose as a field — and `structuralMismatch()`
 * cross-checks it against the API's own metadata so the two cannot silently
 * disagree.
 *
 * ── Editability ─────────────────────────────────────────────────────────────
 * A DRAFT is the editable surface. PUBLISHED and ARCHIVED versions render
 * read-only, and so does the system template, because the backend refuses those
 * writes — offering controls that would 403 is the "pretend an operation is
 * available" failure §4 forbids.
 */
export function ReportTemplateDesignerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const can = useHasPermission();

  const query = useReportTemplate(id);
  const template = query.data;

  const designMutation = useUpdateReportTemplateDesign(id ?? '');
  const publishMutation = usePublishReportTemplate(id ?? '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Keyboard reordering: Space to lift, arrows to move, Space to drop.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Working draft. Not persisted until Save. */
  const [draft, setDraft] = useState<DraftSection[] | null>(null);
  /** The last state the server confirmed — what Cancel restores and dirty compares against. */
  const [saved, setSaved] = useState<DraftSection[] | null>(null);
  const [saveForbidden, setSaveForbidden] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!template) return;
    setSaved(template.sections);
    // Never clobber edits in progress: a background refetch must not discard
    // what the MSE is part-way through changing.
    setDraft((current) => current ?? template.sections);
  }, [template]);

  const mismatch = useMemo(() => (draft ? structuralMismatch(draft) : null), [draft]);

  // ── Read states ────────────────────────────────────────────────────────────

  if (query.isLoading) return <SkeletonPage />;

  if (query.error) {
    const status = statusOf(query.error);
    return (
      <div className="space-y-4" data-testid="designer-error">
        <button
          type="button"
          onClick={() => navigate(ROUTES.REPORT_TEMPLATES)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
          Back to Report Templates
        </button>
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mb-3 h-8 w-8 text-muted-foreground/40"
          />
          <p className="text-base font-medium text-foreground">
            {status === 403
              ? 'You do not have permission to open this report template.'
              : status === 404
                ? 'This report template no longer exists.'
                : 'Could not load this report template.'}
          </p>
          {status !== 403 && status !== 404 && (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                {extractMsg(query.error, 'The server did not return the template.')}
              </p>
              <Button
                variant="secondary"
                onClick={() => void query.refetch()}
                className="mt-4"
                data-testid="retry-designer"
              >
                <FontAwesomeIcon icon={faArrowRotateRight} className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!template || draft === null || saved === null) return <SkeletonPage />;

  // §9 — a design that contradicts D-079 is surfaced, never silently rewritten.
  if (mismatch) {
    return (
      <div className="space-y-4" data-testid="designer-structural-mismatch">
        <button
          type="button"
          onClick={() => navigate(ROUTES.REPORT_TEMPLATES)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
          Back to Report Templates
        </button>
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6">
          <p className="flex items-start gap-2 text-sm text-destructive">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{mismatch}</span>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            The design has not been modified. Nothing was sent to the server.
          </p>
        </div>
      </div>
    );
  }

  // ── Editability ────────────────────────────────────────────────────────────

  const isDraftStatus = template.status === 'DRAFT';
  const readOnlyReason = template.isSystem
    ? 'This is the platform-seeded system template. Clone it to start a design of your own.'
    : template.status === 'PUBLISHED'
      ? 'A published version is immutable. Create a new version to make changes.'
      : template.status === 'ARCHIVED'
        ? 'An archived version is immutable history and can no longer be edited.'
        : null;

  const editable =
    isDraftStatus && !template.isSystem && can('report-template:update') && !saveForbidden;
  const busy = designMutation.isPending || publishMutation.isPending;
  const dirty = !sameDesign(draft, saved);

  // ── Mutators ───────────────────────────────────────────────────────────────

  const patchSection = (sectionKey: string, patch: Partial<DraftSection>) =>
    setDraft((cur) =>
      (cur ?? []).map((s) => (s.sectionKey === sectionKey ? { ...s, ...patch } : s)),
    );

  const resizeSection = (sectionKey: string, columns: ReportTemplateColumn[]) =>
    setDraft((cur) =>
      (cur ?? []).map((s) => (s.sectionKey === sectionKey ? { ...s, columns } : s)),
    );

  /**
   * Reorders across ALL sections.
   *
   * Unlike the SE composer — where structural sections hold their template
   * positions because one report may not move them — the MSE is designing the
   * template itself, and section order is exactly what they own (D-2/D-6). The
   * backend accepts any dense 0..n-1 permutation, so nothing is withheld here.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((cur) => {
      const list = cur ?? [];
      const from = list.findIndex((s) => s.sectionKey === String(active.id));
      const to = list.findIndex((s) => s.sectionKey === String(over.id));
      if (from < 0 || to < 0) return list;
      return arrayMove(list, from, to);
    });
  };

  const handleSave = () => {
    setPublishError(null);
    designMutation.mutate(toDesignPayload(draft), {
      onSuccess: (detail) => {
        // The server's document becomes the new truth, not the local draft.
        setDraft(detail.sections);
        setSaved(detail.sections);
        setSaveForbidden(false);
        toast.success('Design saved');
      },
      onError: (e) => {
        // The draft is deliberately left intact so the MSE's work survives.
        if (statusOf(e) === 403) setSaveForbidden(true);
        toast.error(extractMsg(e, 'Failed to save the design'));
      },
    });
  };

  const handlePublish = async () => {
    const hiddenCount = draft.filter((s) => !s.isVisible).length;
    const toggleableCount = draft.filter((s) => s.isEngineerToggleable).length;
    const ok = await swalConfirm({
      title: `Publish v${template.versionNumber}?`,
      html:
        `<p class="text-sm">A published version becomes immutable.</p>` +
        `<ul class="mt-2 text-left text-xs">` +
        `<li>${draft.length} sections · ${draft.length - hiddenCount} visible · ${hiddenCount} hidden</li>` +
        `<li>${toggleableCount} section(s) an engineer may toggle per report</li>` +
        `<li>Publishing does <strong>not</strong> change the tenant's default template.</li>` +
        `</ul>`,
    });
    if (!ok) return;
    setPublishError(null);
    try {
      await publishMutation.mutateAsync();
      toast.success(`Version ${template.versionNumber} published`);
    } catch (e) {
      // The backend's publish gate is the authority — its message is shown
      // verbatim rather than being re-derived or paraphrased here.
      const message = extractMsg(e, 'Failed to publish this version');
      setPublishError(message);
      toast.error(message);
    }
  };

  const visibleCount = draft.filter((s) => s.isVisible).length;

  return (
    <div className="space-y-6" data-testid="report-template-designer">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.REPORT_TEMPLATES)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="back-to-library"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
          Back to Report Templates
        </button>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Report Template
            </p>
            <h1 className="truncate text-xl font-semibold text-foreground">{template.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground" data-testid="designer-version">
                v{template.versionNumber}
              </span>
              <ReportTemplateStatusBadge status={template.status} />
              {template.isDefault && <ReportTemplateDefaultBadge />}
              {template.isSystem && <ReportTemplateSystemBadge />}
              <span className="font-mono text-[10px] text-muted-foreground">{template.slug}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowPreview((v) => !v)}
              data-testid="toggle-preview"
              aria-expanded={showPreview}
            >
              <FontAwesomeIcon icon={faEye} className="mr-2 h-3.5 w-3.5" />
              {showPreview ? 'Hide preview' : 'Preview'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(ROUTES.REPORT_TEMPLATE_VERSIONS.replace(':id', template.id))}
              data-testid="open-versions"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} className="mr-2 h-3.5 w-3.5" />
              Versions
            </Button>
            {editable && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setDraft(saved)}
                  disabled={busy || !dirty}
                  data-testid="cancel-design"
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={busy || !dirty} data-testid="save-design">
                  {designMutation.isPending ? 'Saving…' : 'Save Draft'}
                </Button>
                {can('report-template:publish') && (
                  <Button
                    onClick={() => void handlePublish()}
                    disabled={busy || dirty}
                    title={dirty ? 'Save your changes before publishing' : undefined}
                    data-testid="publish-design"
                  >
                    <FontAwesomeIcon icon={faCheckDouble} className="mr-2 h-3.5 w-3.5" />
                    Publish
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Read-only / permission notices ───────────────────────────────── */}
      {readOnlyReason && (
        <p
          className="flex items-start gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground"
          data-testid="designer-readonly-notice"
        >
          <FontAwesomeIcon icon={faLock} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{readOnlyReason}</span>
        </p>
      )}

      {isDraftStatus && !template.isSystem && !can('report-template:update') && (
        <p
          className="rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
          data-testid="designer-permission-notice"
        >
          You can view this design but do not have permission to change it.
        </p>
      )}

      {saveForbidden && (
        <p
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          data-testid="designer-save-forbidden"
        >
          The server refused the change: you do not have permission to edit this design. Your
          unsaved edits are still here.
        </p>
      )}

      {publishError && (
        <p
          className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          data-testid="designer-publish-error"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{publishError}</span>
        </p>
      )}

      {/* ── Preview ──────────────────────────────────────────────────────── */}
      {showPreview && <TemplateStructurePreview sections={draft} />}

      {/* ── Sections ─────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">Report Sections</h2>
          <span className="text-xs text-muted-foreground" data-testid="designer-section-count">
            {visibleCount} of {draft.length} visible
          </span>
        </div>

        <p className="text-xs text-muted-foreground">
          Sections render top to bottom in this order. The header, meta grid, classification band
          and signature block are mandatory structure and are always visible.
        </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={draft.map((s) => s.sectionKey)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2" data-testid="designer-sections-list">
              {draft.map((section, i) => (
                <TemplateSectionRow
                  key={section.sectionKey}
                  section={section}
                  index={i}
                  canEdit={editable && !busy}
                  busy={busy}
                  onChange={(patch) => patchSection(section.sectionKey, patch)}
                  onResize={(columns) => resizeSection(section.sectionKey, columns)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </section>

      {/* ── Footer save bar ──────────────────────────────────────────────── */}
      {editable && (
        <div
          className={cn(
            'sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-border bg-background/95 py-3 backdrop-blur',
            !dirty && 'text-muted-foreground',
          )}
        >
          <Button onClick={handleSave} disabled={busy || !dirty} data-testid="save-design-footer">
            {designMutation.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setDraft(saved)}
            disabled={busy || !dirty}
            data-testid="cancel-design-footer"
          >
            Cancel
          </Button>
          {dirty && !busy && (
            <span className="text-xs text-muted-foreground" data-testid="designer-unsaved">
              Unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
}
